// EditNotification.tsx
import React, { useEffect, useState } from "react";
import { X, Info, Lock } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import "./EditNotification.css";
import { useGlobalModal } from "../GlobalModalProvider/GlobalModalProvider";
import {
  NOTIFICATION_TYPE_LABELS,
  TYPE_DAYS_OFFSET_CONFIG,
  NotificationType,
} from "../CriarNotificacaoModal/CriarNotificacaoModal";

export type NotificationTemplateShape = {
  id?: string;
  coworkingId?: string | null;
  title?: string;
  description?: string | null;
  type?: NotificationType | null;
  imageUrl?: string | null;
  intro?: string | null;
  message?: string | null;
  conclusion?: string | null;
  contactLink?: string | null;
  daysOffset?: number;
  active?: boolean;
};

interface EditNotificationProps {
  template: NotificationTemplateShape | null;
  onClose: () => void;
  onSaved?: (updated?: NotificationTemplateShape) => void;
}

export function EditNotification({ template, onClose, onSaved }: EditNotificationProps) {
  const userContext = useUser();
  const token = userContext?.userData?.auth;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [mainMessage, setMainMessage] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [contactLink, setContactLink] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [daysOffset, setDaysOffset] = useState<number>(0);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const modal = useGlobalModal();

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  useEffect(() => {
    if (!template) return;
    setTitle(template.title ?? "");
    setDescription(template.description ?? "");
    setIntroduction(template.intro ?? "");
    setMainMessage(template.message ?? "");
    setConclusion(template.conclusion ?? "");
    setContactLink(template.contactLink ?? "");
    setImageUrl(template.imageUrl ?? "");
    setDaysOffset(template.daysOffset ?? 0);
    setActive(typeof template.active === "boolean" ? template.active : true);
    console.log("Template para editar:", template.id);
  }, [template]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;
    if (!title.trim()) {
      modal?.show({ type: "warning", title: "Campo obrigatório", message: "Preencha o título", autoClose: 3000 });
      return;
    }
    if (!mainMessage.trim()) {
      modal?.show({ type: "warning", title: "Campo obrigatório", message: "Preencha a mensagem principal", autoClose: 3000 });
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        title: title.trim(),
        description: description?.trim() || undefined,
        imageUrl: imageUrl?.trim() || undefined,
        intro: introduction?.trim() || undefined,
        message: mainMessage?.trim() || undefined,
        conclusion: conclusion?.trim() || undefined,
        contactLink: contactLink?.trim() || undefined,
        daysOffset: Math.max(0, Math.floor(Number(daysOffset) || 0)),
        active: Boolean(active),
      };

      const templateId = template.id;
      console.log("Template ID para salvar:", templateId);
      if (!templateId) {
        modal?.show({ type: "error", title: "Erro", message: "ID do template não encontrado.", autoClose: 3000 });
        setSaving(false);
        return;
      }

      const options = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      const resp = await api.put(`/notifications/templates/${templateId}`, payload, options);
      const data = (resp && (resp as any).data) ? (resp as any).data : resp;
      const updated: NotificationTemplateShape = data?.id ? data : { ...template, ...payload };

      modal?.show({ type: "success", title: "Sucesso", message: "Notificação atualizada com sucesso!", autoClose: 3000 });
      if (onSaved) onSaved(updated);
      onClose();
    } catch (err: any) {
      console.error("Erro ao atualizar template:", err);
      const msg = err?.response?.data?.message ?? err?.message ?? "Erro ao atualizar template";
      modal?.show({ type: "error", title: "Erro", message: msg, autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  if (!template) return null;

  const typeLabel = template.type ? NOTIFICATION_TYPE_LABELS[template.type] ?? template.type : "Desconhecido";
  const showDaysOffset = template.type && TYPE_DAYS_OFFSET_CONFIG[template.type]?.show;
  const daysLabel = template.type ? TYPE_DAYS_OFFSET_CONFIG[template.type]?.label : "";

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}><X size={20} /></button>

        <h2 className="modal-title">Editar Notificação</h2>
        <p className="modal-subtitle">Altere os campos da notificação selecionada</p>

        <form onSubmit={handleSubmit}>
          {/* Type display (locked) */}
          <div className="form-group">
            <label>Tipo de Notificação</label>
            <div className="type-locked">
              <Lock size={14} />
              <span>{typeLabel}</span>
            </div>
            <small className="form-hint">O tipo não pode ser alterado após a criação.</small>
          </div>

          {/* Days offset (conditional) */}
          {showDaysOffset && (
            <div className="form-group">
              <label htmlFor="days-offset">{daysLabel}</label>
              <input
                id="days-offset"
                type="number"
                min="0"
                max="365"
                value={daysOffset}
                onChange={e => setDaysOffset(parseInt(e.target.value) || 0)}
                className="form-control days-input"
              />
              <small className="form-hint">Quantos dias antes do evento a notificação será enviada.</small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notif-title">Título da Notificação *</label>
            <input id="notif-title" type="text" value={title} onChange={e => setTitle(e.target.value)} required />
          </div>

          <div className="form-group">
            <label htmlFor="notif-desc">Descrição (interno)</label>
            <textarea id="notif-desc" rows={2} value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <div className="form-group">
            <h3 className="section-title">Estrutura do Email</h3>
            <label htmlFor="notif-image">Imagem (URL)</label>
            <input id="notif-image" type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} />
          </div>

          <div className="form-group">
            <label htmlFor="notif-intro">Introdução</label>
            <input id="notif-intro" type="text" value={introduction} onChange={e => setIntroduction(e.target.value)} />
            <small className="form-hint">Use {"{{customerName}}"} para inserir o nome do cliente.</small>
          </div>

          <div className="form-group">
            <label htmlFor="notif-main">Mensagem Principal *</label>
            <textarea id="notif-main" rows={4} value={mainMessage} onChange={e => setMainMessage(e.target.value)} required />
          </div>

          <div className="form-group">
            <label htmlFor="notif-conclusion">Conclusão</label>
            <input id="notif-conclusion" type="text" value={conclusion} onChange={e => setConclusion(e.target.value)} />
          </div>

          <div className="form-group">
            <label htmlFor="notif-link">Link de Contato</label>
            <input id="notif-link" type="text" value={contactLink} onChange={e => setContactLink(e.target.value)} />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
              <span>Ativo</span>
            </label>
            <small className="form-hint">Apenas templates ativos serão enviados automaticamente.</small>
          </div>

          <div className="modal-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Salvando..." : "Salvar Alterações"}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditNotification;
